import { useCallback, useEffect, useState } from "react"
import { fetchImageBase64, IMAGE_TYPES } from "monitor/Configurator/service"
import { toImageDataUrl, getCachedImage, setCachedImage } from "utils/remoteImageCache"

const BACKGROUND_IMAGE_CACHE_KEY = "app.backgroundImage.dataUrl"
const BACKGROUND_IMAGE_CACHE_TS_KEY = "app.backgroundImage.ts"
const BACKGROUND_IMAGE_CACHE_TTL_MS = 1000 * 60 * 30 // 30 min

function useBackgroundImage(fallbackImage) {
  const [backgroundImage, setBackgroundImage] = useState(() => {
    const cached = getCachedImage({
      key: BACKGROUND_IMAGE_CACHE_KEY,
      tsKey: BACKGROUND_IMAGE_CACHE_TS_KEY,
      ttlMs: BACKGROUND_IMAGE_CACHE_TTL_MS,
    })
    return cached || fallbackImage
  })

  const fetchAndCacheBackgroundImage = useCallback(async () => {
    try {
      const companyId = (sessionStorage.getItem("companyId") || localStorage.getItem("companyId") || "").trim()
      if (!companyId) {
        setBackgroundImage(fallbackImage)
        return
      }
      const payload = await fetchImageBase64(IMAGE_TYPES.BACKGROUND, companyId)
      const nextBackgroundImage = toImageDataUrl(payload)

      setCachedImage({
        key: BACKGROUND_IMAGE_CACHE_KEY,
        tsKey: BACKGROUND_IMAGE_CACHE_TS_KEY,
        value: nextBackgroundImage,
      })
      setBackgroundImage(nextBackgroundImage || fallbackImage)
    } catch {
      setBackgroundImage(fallbackImage)
    }
  }, [fallbackImage])

  useEffect(() => {
    const cached = getCachedImage({
      key: BACKGROUND_IMAGE_CACHE_KEY,
      tsKey: BACKGROUND_IMAGE_CACHE_TS_KEY,
      ttlMs: BACKGROUND_IMAGE_CACHE_TTL_MS,
    })

    if (cached !== undefined) {
      setBackgroundImage(cached || fallbackImage)
      return
    }

    fetchAndCacheBackgroundImage()
  }, [fetchAndCacheBackgroundImage, fallbackImage])

  useEffect(() => {
    const handleBackgroundRefresh = () => {
      fetchAndCacheBackgroundImage()
    }

    window.addEventListener("background-image:refresh", handleBackgroundRefresh)
    return () => {
      window.removeEventListener("background-image:refresh", handleBackgroundRefresh)
    }
  }, [fetchAndCacheBackgroundImage])

  return backgroundImage
}

export default useBackgroundImage
